import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { startImpersonation } from "@/lib/impersonation";
import type { AdminOrgMeta, MemberMeta } from "@shared/protocol";
import { useSession } from "@/components/layout/AppShell";
import { relativeTime } from "@/lib/utils";

/** Super-admin-only: one org's detail — its member list, with the option
 * to create/delete users, reset anyone's password, impersonate one, and
 * delete the org itself. Reached by clicking a row on OrganizationsPage.tsx. */
export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const [org, setOrg] = useState<AdminOrgMeta | null>(null);
  const [members, setMembers] = useState<MemberMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<"owner" | "admin" | "member">(
    "member",
  );
  const [creating, setCreating] = useState(false);

  const [passwordTarget, setPasswordTarget] = useState<MemberMeta | null>(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);

  const [deletingOrg, setDeletingOrg] = useState(false);

  function load() {
    if (!id) return;
    api<{ ok: true; org: AdminOrgMeta; members: MemberMeta[] }>(
      `/api/admin/orgs/${id}`,
    )
      .then((res) => {
        setOrg(res.org);
        setMembers(res.members);
      })
      .catch((err) =>
        setError(
          err instanceof ApiError ? err.message : "failed to load organization",
        ),
      );
  }

  useEffect(load, [id]);

  async function handleImpersonate(memberId: string) {
    if (!id) return;
    if (
      !confirm(
        "Impersonate this user? You will act as them until you exit impersonation.",
      )
    )
      return;
    setImpersonatingId(memberId);
    try {
      await startImpersonation(id, memberId);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "failed to start impersonation",
      );
      setImpersonatingId(null);
    }
  }

  async function handleCreateUser() {
    if (!id) return;
    setCreating(true);
    try {
      const res = await api<{ ok: true; can_login: boolean }>(
        `/api/admin/orgs/${id}/users`,
        {
          method: "POST",
          body: JSON.stringify({
            email: newEmail.trim(),
            name: newName.trim() || undefined,
            password: newPassword || undefined,
            role: newRole,
          }),
        },
      );
      toast(
        res.can_login
          ? "User created."
          : "User created — no password set yet, they cannot sign in.",
      );
      setShowCreate(false);
      setNewEmail("");
      setNewName("");
      setNewPassword("");
      setNewRole("member");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to create user");
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteUser(memberId: string) {
    if (
      !confirm(
        "Delete this user entirely? This removes their account and every org membership, not just this org.",
      )
    )
      return;
    setBusyId(memberId);
    try {
      await api(`/api/admin/users/${memberId}`, { method: "DELETE" });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to delete user");
    } finally {
      setBusyId(null);
    }
  }

  async function handleSetPassword() {
    if (!passwordTarget) return;
    setSettingPassword(true);
    try {
      await api(`/api/admin/users/${passwordTarget.user_id}/password`, {
        method: "POST",
        body: JSON.stringify({ password: passwordValue }),
      });
      toast("Password updated.");
      setPasswordTarget(null);
      setPasswordValue("");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "failed to set password",
      );
    } finally {
      setSettingPassword(false);
    }
  }

  async function handleDeleteOrg() {
    if (!id || !org) return;
    if (
      !confirm(
        `Delete "${org.name}" permanently? This removes the org, its members, keys, and credentials.`,
      )
    )
      return;
    setDeletingOrg(true);
    try {
      await api(`/api/admin/orgs/${id}`, { method: "DELETE" });
      navigate("/admin/orgs");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "failed to delete organization",
      );
      setDeletingOrg(false);
    }
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/admin/orgs")}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Organizations
          </button>
          <h1 className="text-2xl font-semibold mt-1">{org?.name ?? "…"}</h1>
          {org && (
            <p className="text-sm text-muted-foreground">
              {org.slug} {org.is_super && <Badge className="ml-1">Super</Badge>}
            </p>
          )}
        </div>
        {org && !org.is_super && (
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            disabled={deletingOrg}
            onClick={handleDeleteOrg}
          >
            {deletingOrg ? "Deleting…" : "Delete organization"}
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Users{members ? ` (${members.length})` : ""}</CardTitle>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            New user
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members?.map((m) => {
                const isSelf = m.user_id === user.id;
                const isBusy = busyId === m.user_id;
                return (
                  <TableRow key={m.user_id}>
                    <TableCell>{m.name ?? "—"}</TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{m.role}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(m.joined_at)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isSelf || impersonatingId === m.user_id}
                        title={
                          isSelf ? "You can't impersonate yourself" : undefined
                        }
                        onClick={() => handleImpersonate(m.user_id)}
                      >
                        {impersonatingId === m.user_id
                          ? "Starting…"
                          : "Impersonate"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPasswordTarget(m)}
                      >
                        Set password
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={isSelf || isBusy}
                        title={
                          isSelf
                            ? "You can't delete your own account"
                            : undefined
                        }
                        onClick={() => handleDeleteUser(m.user_id)}
                      >
                        {isBusy ? "Deleting…" : "Delete"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {members?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No users in this org.
            </p>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Need to manage roles instead?{" "}
        <Link to="/admin/roles" className="underline">
          Roles page
        </Link>
        .
      </p>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create user</DialogTitle>
            <DialogDescription>
              Leave password blank to create the account without one — they
              won't be able to sign in until you set one (from this list, "Set
              password").
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-name">Name (optional)</Label>
              <Input
                id="new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">Password (optional)</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Leave blank for no login access yet"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-role">Role</Label>
              <Select
                id="new-role"
                value={newRole}
                onChange={(e) =>
                  setNewRole(e.target.value as "owner" | "admin" | "member")
                }
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={
                !newEmail.includes("@") ||
                (newPassword.length > 0 && newPassword.length < 10) ||
                creating
              }
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={passwordTarget !== null}
        onOpenChange={(o) => !o && setPasswordTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set password</DialogTitle>
            <DialogDescription>
              Sets (or replaces){" "}
              <span className="font-medium">{passwordTarget?.email}</span>
              &apos;s password directly. They aren&apos;t notified — share the
              new password with them yourself.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 my-4">
            <Label htmlFor="set-password">New password</Label>
            <Input
              id="set-password"
              type="password"
              value={passwordValue}
              onChange={(e) => setPasswordValue(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={passwordValue.length < 10 || settingPassword}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
